import { useState } from 'react';

import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router';

import { listSectionPeriodsOptions } from '@drivingdoc/api-client/generated/react-query';
import { SectionPeriod } from 'types/sections';

dayjs.extend(utc);
dayjs.extend(timezone);

const columns: GridColDef<SectionPeriod>[] = [
  {
    field: 'date',
    type: 'dateTime',
    headerName: 'Date',
    minWidth: 150,
    valueGetter: (_, row) => new Date(row.start),
    valueFormatter: (_, row) => dayjs(row.start).format('MMM Do YYYY'),
  },
  {
    field: 'start',
    type: 'dateTime',
    headerName: 'Start',
    minWidth: 100,
    valueGetter: (_, row) => new Date(row.start),
    valueFormatter: (_, row) => dayjs(row.start).format('h:mm a'),
    // disable filtering since this column is just for display purposes
    // filter on date column instead
    filterable: false,
  },
  {
    field: 'end',
    type: 'dateTime',
    headerName: 'End',
    minWidth: 100,
    valueGetter: (_, row) => new Date(row.end),
    valueFormatter: (_, row) => dayjs(row.end).format('h:mm a'),
    // disable filtering since this column is just for display purposes
    // filter on date column instead
    filterable: false,
  },
  {
    field: 'open_enrollment',
    type: 'dateTime',
    headerName: 'Open Enrollment',
    minWidth: 250,
    valueGetter: (_, row) => (row.open_enrollment ? new Date(row.open_enrollment) : null),
    valueFormatter: (_, row) => (row.open_enrollment ? dayjs(row.open_enrollment).format('MMM Do YYYY, h:mm a') : null),
  },
  {
    field: 'close_enrollment',
    type: 'dateTime',
    headerName: 'Close Enrollment',
    minWidth: 250,
    valueGetter: (_, row) => (row.close_enrollment ? new Date(row.close_enrollment) : null),
    valueFormatter: (_, row) =>
      row.close_enrollment ? dayjs(row.close_enrollment).format('MMM Do YYYY, h:mm a') : null,
  },
  {
    field: 'enrollment_count',
    type: 'number',
    headerName: 'Enrollment Count',
    minWidth: 150,
    valueGetter: (_, row) => Number(row.enrollment_count),
  },
  {
    field: 'waitlist_count',
    type: 'number',
    headerName: 'Waitlist Count',
    minWidth: 150,
    valueGetter: (_, row) => Number(row.waitlist_count),
  },
];

export interface PeriodListProps {
  sectionId: string;
}

export function PeriodList({ sectionId }: PeriodListProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const navigate = useNavigate();

  // get section periods
  const sections = useQuery({
    ...listSectionPeriodsOptions({
      path: {
        id: sectionId,
      },
      query: {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      },
    }),
    // use previous data as placeholder to maintain a stable
    // reference while fetching the next page so the datagrid
    // doesn't reset the pagination model
    placeholderData: keepPreviousData,
    enabled: !!sectionId,
  });
  const sectionData = sections.data as { data: SectionPeriod[]; total: number } | undefined;

  return (
    <DataGrid
      loading={sections.isFetching}
      columns={columns}
      rows={sectionData?.data}
      initialState={{
        columns: {
          columnVisibilityModel: {
            open_enrollment: false,
            close_enrollment: false,
          },
        },
      }}
      onRowClick={(params) => navigate(`/admin/periods/${params.row.id}`)}
      // enable serverside pagination
      paginationMode="server"
      rowCount={sectionData?.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      disableColumnFilter
      disableColumnSorting
    />
  );
}

export default PeriodList;
